import { useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { DateTime } from 'luxon';
import { Card, CardBody, Flex, Text } from '@backstage/ui';
import { useCheckHistory } from '../../hooks';
import { HistoryChartHeader } from '../history-chart-header';
import { historyChartBodyStyle } from '../history-chart-style';

const formatDate = (dateStr: string) =>
  DateTime.fromISO(dateStr).toFormat('MMM d');

type Props = {
  checkId: string;
  /**
   * When true, the entity filter on the details page is active. History is
   * always computed across all entities, so we flag it as not reflecting the
   * current filter.
   */
  isFiltered?: boolean;
};

export const CheckHistoryChartCard = ({
  checkId,
  isFiltered = false,
}: Props) => {
  const [selectedDays, setSelectedDays] = useState(30);

  const {
    value: history,
    loading,
    error,
  } = useCheckHistory(checkId, selectedDays);

  return (
    <Card style={{ height: '100%' }}>
      <HistoryChartHeader
        isFiltered={isFiltered}
        selectedDays={selectedDays}
        onSelectedDaysChange={setSelectedDays}
      />
      <CardBody>
        <Flex
          direction="column"
          gap="2"
          style={historyChartBodyStyle(isFiltered)}
        >
          {loading && (
            <Text color="secondary" variant="body-small">
              Loading…
            </Text>
          )}

          {error && (
            <Text color="danger" variant="body-small">
              {error.message}
            </Text>
          )}

          {!loading && !error && (!history || history.length === 0) && (
            <Text color="secondary">No data available</Text>
          )}

          {!loading && !error && history && history.length > 0 && (
            <ResponsiveContainer width="100%" height={140}>
              <LineChart
                data={history}
                margin={{ top: 4, right: 4, left: -20, bottom: 0 }}
              >
                <XAxis
                  dataKey="date"
                  tickFormatter={formatDate}
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip
                  labelFormatter={label => formatDate(String(label))}
                  formatter={(value, name) => [
                    value,
                    name === 'passed' ? 'Passed' : 'Failed',
                  ]}
                />
                <Legend
                  formatter={value =>
                    value === 'passed' ? 'Passed' : 'Failed'
                  }
                />
                <Line
                  type="monotone"
                  dataKey="passed"
                  stroke="var(--bui-fg-positive)"
                  dot={false}
                  strokeWidth={2}
                />
                <Line
                  type="monotone"
                  dataKey="failed"
                  stroke="var(--bui-fg-negative)"
                  dot={false}
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Flex>
      </CardBody>
    </Card>
  );
};
